import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AddWorkoutPopup from "./AddWorkoutPopup";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import EditWorkoutPopup from "./EditWorkoutPopup";
import { IconButton, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  delete_program_workout_Api,
  delete_program_workout_Api_all,
  paste_multiple_workouts,
  programme_workout_list_api,
  reorder_events_api,
  update_program_workout_api,
} from "src/DAL/programmeWorkout/programmeWorkout";
import { programme_detail_api } from "src/DAL/Programme/Programme";
import { useSnackbar } from "notistack";
import ConfirmationWithDescription from "src/components/ModalPopover/ConfirmationWithDescription";
import { Icon } from "@iconify/react";
import { s3baseUrl } from "src/config/config";
import { makeStyles } from "@mui/styles";
import { show_proper_words, SHOW_PASTE_CONFIRMATION } from "src/utils/constant";
import PastedWorkouts from "src/components/GeneralComponents/PastedWorkouts";
import CustomPopoverNew from "src/components/CustomPopoverNew";
import { useContentSetting } from "src/Hooks/ContentSettingState";
import ReviewsDate from "src/components/GeneralComponents/ReviewsDate";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));
const DynamicTable = () => {
  const navigate = useNavigate();
  const { id, slug } = useParams();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);

  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  const handleModalProcessed = React.useCallback(
    ({ type, date, workoutId: wId }) => {
      const params = new URLSearchParams(searchParamsRef.current);
      if (type === "close") {
        params.delete("modal");
        params.delete("workoutId");
        // Keep the date param so calendar stays on the same day after modal closes
      } else if (type === "add") {
        params.set("modal", "add");
        params.set("date", date);
        params.delete("workoutId");
      } else if (type === "edit") {
        params.set("modal", "edit");
        params.set("workoutId", wId);
        params.set("date", date);
      }
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  const urlModal = searchParams.get("modal");
  const urlDate = searchParams.get("date");
  const urlWorkoutId = searchParams.get("workoutId");

  // Validate URL params — date here is workout_day (a positive integer)
  const isValidUrlDate =
    urlDate && !isNaN(parseInt(urlDate)) && parseInt(urlDate) > 0;

  // If date is invalid, clean up URL params immediately
  useEffect(() => {
    if ((urlModal === "add" || urlModal === "edit") && !isValidUrlDate) {
      const params = new URLSearchParams(searchParams);
      params.delete("modal");
      params.delete("workoutId");
      params.delete("date");
      setSearchParams(params, { replace: true });
    }
  }, []);

  const initialDrawerState =
    urlModal === "add" && isValidUrlDate ? true : false;
  const initialDrawerDay =
    urlModal === "add" && isValidUrlDate ? parseInt(urlDate) : undefined;
  const initialEditState =
    urlModal === "edit" && urlWorkoutId && isValidUrlDate ? true : false;
  const initialEditWorkout =
    urlModal === "edit" && urlWorkoutId ? urlWorkoutId : undefined;

  const { data: programmeData, isLoading } = useQuery({
    queryKey: ["programme_detail", id],
    enabled: !!id,
    queryFn: async () => {
      const result = await programme_detail_api(id);
      if (result.code === 200) {
        return result.program;
      } else {
        throw new Error(result.message);
      }
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const Table = React.useMemo(
    () =>
      React.memo(function Table({
        numRows,
        numCols,
        initialDrawerState,
        initialDrawerDay,
        initialEditState,
        initialEditWorkout,
        onModalProcessed = () => {},
      }) {
        const rowRef = useRef(null); // kept for potential future use
        const toolbarRowRef = useRef(null);
        const [hoveredCell, setHoveredCell] = useState(null);
        const [drawerState, setDrawerState] = useState(
          initialDrawerState || false,
        );
        const [editItrationDrawerState, setEditItrationDrawerState] = useState(
          initialEditState || false,
        );
        const { enqueueSnackbar } = useSnackbar();
        const [editWorkputData, setEditWorkputData] = useState(
          initialEditWorkout || undefined,
        );
        const [openDelete, setOpenDelete] = useState(false);
        const [selectedDate, setSelectDate] = useState(initialDrawerDay || "");
        const [dataDel, setDataDel] = useState();
        const [workouts, setworkouts] = useState([]);
        const [dataCopy, setDataCopy] = useState();
        const [pasteDate, setPasteDate] = useState();
        const [tableData, setTableData] = useState(
          Array(numRows).fill(Array(numCols).fill("")),
        );
        const [openMultiDelete, setOpenMultiDelete] = useState(false);
        const [isPastedWorkouts, setIsPastedWorkouts] = useState(false);
        const [openDragConfig, setOpenDragConfig] = useState(false);
        const [DragConfigDate, setDragConfigDate] = useState(false);
        const [DragConfigData, setDragConfigData] = useState(false);
        const [openCopyPasteConfig, setOpenCopyPasteConfig] = useState(false);
        const [selectedWorkouts, setSelectedWorkouts] = useState([]);
        const [isReviewOpen, setIsReviewOpen] = useState(false);
        const [isPasted, setIsPasted] = useState(false);

        const [pastedWorkouts, setPastedWorkouts] = useState([]);
        const [difficultyLevel, setDifficultyLevel] = useState([]);
        const [globalOpen, setGlobalOpen] = useState(true);
        // Ref so the data-loading callback can read the latest value synchronously
        const globalOpenRef = useRef(true);
        useEffect(() => {
          globalOpenRef.current = globalOpen;
        }, [globalOpen]);
        const {
          setIsOpenNotePad,
          setSelectedNotepad,
          setPageTitle,
          setShowNavBackButton,
        } = useContentSetting();
        const queryClient = useQueryClient();

        const Levels = React.useMemo(() => {
          return difficultyLevel.length > 0
            ? difficultyLevel.map((level) => level).join(", ")
            : "";
        }, [difficultyLevel]);

        const handleOpen = () => {
          let data = {
            title: programmeData?.title,
            _id: programmeData?._id,
            message:
              "This notepad is specific to this programme only and will not affect its members' notepad.",
          };
          setSelectedNotepad(data);
          setIsOpenNotePad(true);
        };

        useEffect(() => {
          if (programmeData?.title) {
            setPageTitle(programmeData.title);
          }
          setShowNavBackButton(true);
          return () => {
            setPageTitle("");
            setShowNavBackButton(false);
          };
        }, [programmeData?.title]);

        const handleCloseAddModal = () => {
          setDrawerState(false);
          onModalProcessed({ type: "close" });
        };

        const handleCloseEditModal = () => {
          setEditItrationDrawerState(false);
          onModalProcessed({ type: "close" });
        };

        const handleCellHover = (rowIndex, colIndex) => {
          setHoveredCell({ rowIndex, colIndex });
        };
        const handleCellLeave = () => {
          setHoveredCell(null);
        };
        const handleAgreeDelete = async (data) => {
          setDataDel(data);
          setOpenDelete(true);
        };

        const handleDelete = async (value) => {
          setOpenDelete(false);

          const result = await delete_program_workout_Api(
            dataDel?.program_workout_slug,
          );
          if (result.code === 200) {
            setEditWorkputData();

            enqueueSnackbar(result.message, { variant: "success" });
            let updatedEvents = workouts.filter(
              (item) =>
                item.program_workout_slug != dataDel.program_workout_slug,
            );
            setworkouts(updatedEvents);
            setPastedWorkouts([]);
            setSelectedWorkouts([]);
            setDataCopy(false);
            queryClient.invalidateQueries({ queryKey: ["program_workouts"] });
          } else {
            enqueueSnackbar(result.message, { variant: "error" });
          }
        };

        const handleDeleteMultipul = async () => {
          setOpenMultiDelete(false);
          const postData = {
            program_workout_slugs: selectedWorkouts.map(
              (workout) => workout?.program_workout_slug,
            ),
          };
          const result = await delete_program_workout_Api_all(postData);
          if (result.code === 200) {
            enqueueSnackbar(result.message, { variant: "success" });
            let updatedEvents = workouts.filter(
              (item) =>
                !postData.program_workout_slugs.includes(
                  item.program_workout_slug,
                ),
            );
            setworkouts(updatedEvents);
            setEditWorkputData();
            setPastedWorkouts([]);
            setSelectedWorkouts([]);
            setDataCopy(false);
            queryClient.invalidateQueries({ queryKey: ["program_workouts"] });
          } else {
            enqueueSnackbar(result.message, { variant: "error" });
          }
        };

        const handleOpenDrawer = (week, day) => {
          let date = (week - 1) * 7 + day;
          setSelectDate(date);
          setDrawerState(true);
          onModalProcessed({ type: "add", date });
        };
        const handleCopyExercise = (data, rowIndex, colIndex) => {
          setEditWorkputData();
          setPastedWorkouts();
          setSelectedWorkouts((prevSelected) => {
            const exists = prevSelected.some(
              (workout) => workout?._id === data._id,
            );

            const updatedSelected = exists
              ? prevSelected.filter((workout) => workout?._id !== data._id)
              : [...prevSelected, { ...data, rowIndex, colIndex }];

            setDataCopy(updatedSelected.length > 0);

            const sortedSelected = updatedSelected.sort((a, b) => {
              if (a.rowIndex !== b.rowIndex) {
                return a.rowIndex - b.rowIndex;
              } else {
                return a.colIndex - b.colIndex;
              }
            });

            return sortedSelected;
          });
        };
        const calculateIntervals = (days) => {
          const intervals = [];
          for (let i = 1; i < days.length; i++) {
            const interval = days[i] - days[i - 1];
            intervals.push(interval);
          }
          return intervals;
        };
        function generateDates(intervals, pastedDay, selected) {
          const result = [];
          let currentDay = pastedDay;

          for (let i = 0; i < selected.length; i++) {
            if (i === 0) {
              result.push(currentDay);
            } else {
              const interval = intervals[i - 1] || 0;
              currentDay += interval;
              result.push(currentDay);
            }
          }

          return result;
        }

        const isWorkoutSelected = (workout) =>
          selectedWorkouts.some((item) => item._id === workout?._id) ||
          (pastedWorkouts &&
            pastedWorkouts.some((item) => item._id === workout?._id));

        const handlePaste = async (week, day) => {
          if (isPasted) return;

          const pasted = (week - 1) * 7 + day;
          const dates =
            selectedWorkouts &&
            selectedWorkouts.map((workout) => workout?.workout_day);
          const sorthedDates = dates.sort((a, b) => a - b);
          const intervalsAll = calculateIntervals(sorthedDates);
          const datesToBePasted = generateDates(
            intervalsAll,
            pasted,
            sorthedDates,
          );

          const hasError = datesToBePasted.some(
            (day) => day > programmeData?.no_of_weeks * 7,
          );
          if (hasError) {
            enqueueSnackbar(
              "Pasting is not allowed on dates beyond the calendar's range",
              {
                variant: "error",
              },
            );
            setPastedWorkouts([]);
            setSelectedWorkouts([]);
            setDataCopy(false);
          } else {
            const targetDay = (week - 1) * 7 + day;
            setPasteDate(targetDay);
            if (SHOW_PASTE_CONFIRMATION) {
              setOpenCopyPasteConfig(true);
            } else {
              await handleCopyPaste(targetDay);
            }
          }
        };
        const handleOpenEditIterationDrawer = () => {
          setEditItrationDrawerState(true);
        };
        const handleUpdateIteration = (data) => {
          setEditWorkputData(data);
          setSelectDate(data?.workout_day);
          handleOpenEditIterationDrawer();
          onModalProcessed({
            type: "edit",
            date: data?.workout_day,
            workoutId: data?._id,
          });
        };
        const handleReorder = async (newevents) => {
          let postData = {
            workoutIds: newevents,
          };
          const result = await reorder_events_api(postData);
          if (result.code === 200) {
          } else {
            enqueueSnackbar(result.message, { variant: "error" });
          }
        };
        const EventCard = ({ data, setData, index, rowIndex, colIndex }) => {
          const [isHovered, setIsHovered] = useState(false);

          const timeoutRef = useRef(null);

          useEffect(() => {
            return () => {
              clearTimeout(timeoutRef.current);
            };
          }, []);

          const formatWithLeadingZeros = (value) => {
            return value < 10 ? `0${value}` : `${value}`;
          };
          const getMinutesForInterval = (mainindex, index) => {
            const list = [...data?.exercises];
            const interval = list[mainindex].interval_duration;
            if (interval <= 60) {
              return index + 1;
            }
            if (interval % 60 == 0) {
              return `${
                (interval / 60) * index +
                1 +
                " – " +
                ((interval / 60) * index + interval / 60)
              }`;
            }
            let startInterval = interval * index;
            let endtInterval = interval * index + interval;
            let startintervalText =
              interval * index
                ? Math.floor(startInterval / 60) +
                  " " +
                  ":" +
                  formatWithLeadingZeros(startInterval % 60)
                : 0;
            let endIntervalText =
              Math.floor(endtInterval / 60) +
              " " +
              ":" +
              formatWithLeadingZeros(endtInterval % 60);

            return `${startintervalText + " – " + endIntervalText}`;
          };
          const handleMouseEnter = () => {
            clearTimeout(timeoutRef.current);
            setIsHovered(true);
          };

          const isFirstEventOnDate = (workout) => {
            const sameDayWorkouts = workouts.filter(
              (w) => w.workout_day === workout?.workout_day,
            );
            return sameDayWorkouts[0]._id === workout?._id;
          };

          const isLastEventOnDate = (workout) => {
            const sameDayWorkouts = workouts.filter(
              (w) => w.workout_day === workout?.workout_day,
            );
            return (
              sameDayWorkouts[sameDayWorkouts.length - 1]._id === workout?._id
            );
          };
          const updateSortedIds = (workoutDay, updatedWorkouts) => {
            const sortedIds = updatedWorkouts
              .filter((w) => w.workout_day === workoutDay)
              .map((w) => w._id);
            handleReorder(sortedIds);
          };

          const moveEventUp = (workout) => {
            setEditWorkputData(workout);
            const sameDayWorkouts = workouts.filter(
              (w) => w.workout_day === workout?.workout_day,
            );
            const workoutIndex = sameDayWorkouts.findIndex(
              (w) => w._id === workout?._id,
            );
            if (workoutIndex > 0) {
              const newWorkouts = [...workouts];
              const prevWorkout = sameDayWorkouts[workoutIndex - 1];
              const currentWorkoutIndex = workouts.findIndex(
                (w) => w._id === workout?._id,
              );
              const prevWorkoutIndex = workouts.findIndex(
                (w) => w._id === prevWorkout._id,
              );
              newWorkouts[currentWorkoutIndex] = prevWorkout;
              newWorkouts[prevWorkoutIndex] = workout;
              setworkouts(newWorkouts);
              updateSortedIds(workout?.workout_day, newWorkouts);
            }
          };

          const moveEventDown = (workout) => {
            setEditWorkputData(workout);

            const sameDayWorkouts = workouts.filter(
              (w) => w.workout_day === workout?.workout_day,
            );
            const workoutIndex = sameDayWorkouts.findIndex(
              (w) => w._id === workout?._id,
            );
            if (workoutIndex < sameDayWorkouts.length - 1) {
              const newWorkouts = [...workouts];
              const nextWorkout = sameDayWorkouts[workoutIndex + 1];
              const currentWorkoutIndex = workouts.findIndex(
                (w) => w._id === workout?._id,
              );
              const nextWorkoutIndex = workouts.findIndex(
                (w) => w._id === nextWorkout._id,
              );
              newWorkouts[currentWorkoutIndex] = nextWorkout;
              newWorkouts[nextWorkoutIndex] = workout;
              setworkouts(newWorkouts);
              updateSortedIds(workout?.workout_day, newWorkouts);
            }
          };

          const handleMouseLeave = () => {
            const delay = 300;
            timeoutRef.current = setTimeout(() => {
              setIsHovered(false);
            }, delay);
          };

          return (
            <div
              className="calendar-workout-card my-4"
              style={{
                boxShadow: isWorkoutSelected(data)
                  ? "-2px -2px 7px 1px #04abbb, 1px 1px 7px 2px #04abbb"
                  : "",
                border:
                  editWorkputData?._id == data?._id
                    ? "2px solid var(--portal-theme-primary)"
                    : "0px solid var(--portal-theme-primary)",
                borderTopRightRadius: "7px",
                borderTopLeftRadius: "7px",
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div className="w-100 h-100 " onMouseEnter={handleMouseEnter}>
                {isHovered && (
                  <div className="all-icon-calendar-workout-general d-flex w-100 justify-content-between">
                    <div className="d-flex">
                      <Tooltip
                        title={`${
                          data?.workout_loggers?.length == 0
                            ? "Delete"
                            : "Can't be deleted, This workout has results."
                        }`}
                      >
                        <DeleteIcon
                          className={`${
                            data?.workout_loggers?.length == 0
                              ? "del-icon-calendar-workout-general "
                              : "del-icon-calendar-workout-disabled-general"
                          }`}
                          onClick={
                            data?.workout_loggers?.length == 0
                              ? () => handleAgreeDelete(data)
                              : () => {}
                          }
                        />
                      </Tooltip>
                      <Tooltip title={`${"Copy"}`}>
                        <div
                          className={`${"copy-icon-calendar-workout-general ms-1 d-flex justify-content-center align-items-center"}`}
                        >
                          <ContentCopyIcon
                            sx={{ width: 17, height: 17 }}
                            onClick={() =>
                              handleCopyExercise(data, rowIndex, colIndex)
                            }
                          />
                        </div>
                      </Tooltip>
                    </div>{" "}
                    <div className="d-flex">
                      {" "}
                      <Tooltip title="Move Up">
                        <div className="arrow-icon-calendar-workout-general ms-1 d-flex justify-content-center align-items-center">
                          <IconButton disabled={isFirstEventOnDate(data)}>
                            <KeyboardArrowUpIcon
                              sx={{ width: 17, height: 17 }}
                              onClick={() => moveEventUp(data)}
                            />
                          </IconButton>
                        </div>
                      </Tooltip>{" "}
                      <Tooltip title="Move Down">
                        <div className="arrow-icon-calendar-workout-general ms-1 d-flex justify-content-center align-items-center">
                          <IconButton disabled={isLastEventOnDate(data)}>
                            <KeyboardArrowDownIcon
                              sx={{ width: 17, height: 17 }}
                              onClick={() => moveEventDown(data)}
                            />
                          </IconButton>
                        </div>
                      </Tooltip>{" "}
                    </div>
                  </div>
                )}
              </div>

              <div onMouseEnter={handleMouseEnter}>
                <div
                  className="calendar-workout-card-title"
                  style={{
                    backgroundColor: data?.program_info?.color,
                  }}
                  onMouseEnter={handleMouseEnter}
                >
                  <div
                    onMouseEnter={handleMouseEnter}
                    className="calendar-workout-card-program"
                  >
                    {data?.program_info?.title}
                  </div>
                  <div className="d-flex justify-content-between">
                    <div
                      className="calendar-workout-card-workout"
                      onClick={() => handleUpdateIteration(data)}
                      onMouseEnter={handleMouseEnter}
                    >
                      {data?.title}
                    </div>
                    <div className="d-flex align-items-center">
                      <Icon
                        icon={
                          data?.practice_type == "workout" ||
                          data?.library_type == "workout-type"
                            ? "mdi:gym"
                            : data?.practice_type == "food" ||
                              data?.library_type == "meal-type"
                            ? "fluent:food-24-filled"
                            : data?.practice_type == "mindset" ||
                              data?.library_type == "mindset-type"
                            ? "flowbite:brain-outline"
                            : data?.practice_type == "video"
                            ? "lets-icons:video-fill"
                            : data.practice_type == "comment"
                            ? "mdi:comment-text"
                            : "mdi:gym"
                        }
                        className="workout-type-icon-container "
                      />
                      {!data?.open ? (
                        <KeyboardArrowDownIcon
                          sx={{
                            cursor: "pointer",
                            opacity: 0.7,
                            fontSize: "20px",
                          }}
                          onClick={() => {
                            const list = [...workouts];
                            list[index]["open"] = true;
                            setData(list);
                          }}
                        />
                      ) : (
                        <KeyboardArrowUpIcon
                          sx={{
                            cursor: "pointer",
                            opacity: 0.7,
                            fontSize: "20px",
                          }}
                          onClick={() => {
                            const list = [...workouts];
                            list[index]["open"] = false;
                            setData(list);
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
                {data?.open && (
                  <div className="calendar-workout-card-exercises">
                    <div
                      className="calendar-workout-card-container"
                      onMouseEnter={handleMouseEnter}
                      onClick={() => handleUpdateIteration(data)}
                    >
                      {data.practice_type == "workbook" ? (
                        <>
                          <div
                            className="inner-div workout-calendar-exercise-card my-1"
                            style={{
                              whiteSpace: "break-spaces",
                            }}
                          >
                            {data?.title}
                          </div>
                        </>
                      ) : (
                        <></>
                      )}
                      {data?.practice_type == "video" ? (
                        <>
                          <div className="inner-div workout-calendar-exercise-card my-1">
                            {data?.dumyImage ? (
                              <div
                                style={{
                                  whiteSpace: "break-spaces",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <img
                                  className="image-border"
                                  src={data?.dumyImage}
                                  height="50"
                                />
                              </div>
                            ) : (
                              <div
                                style={{
                                  whiteSpace: "break-spaces",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <img
                                  className="image-border"
                                  src={
                                    s3baseUrl +
                                    data?.video_thumbnail?.thumbnail_1
                                  }
                                  height="50"
                                />
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <></>
                      )}
                      {data?.practice_type == "comment" ? (
                        <>
                          <div
                            className="inner-div workout-calendar-exercise-card my-1"
                            style={{
                              fontSize: "11px",
                              overflow: "hidden",
                              whiteSpace: "break-spaces",
                            }}
                          >
                            {data?.comment || ""}
                          </div>
                        </>
                      ) : (
                        <></>
                      )}
                      {data?.exercises.map((exercise, index) => {
                        if (exercise?.exercise_type == "something_else") {
                          return (
                            <div className="inner-div workout-calendar-exercise-card my-1">
                              <div
                                style={{
                                  whiteSpace: "break-spaces",
                                }}
                              >
                                {exercise?.title}
                              </div>
                            </div>
                          );
                        } else if (
                          exercise?.exercise_type == "rounds_for_time" &&
                          exercise?.emom_amrap &&
                          exercise?.emom_amrap.length > 0
                        ) {
                          return (
                            <div className="workout-calendar-Superset-card inner-div my-1">
                              <div className="text-center">
                                {exercise?.is_completed && (
                                  <Tooltip
                                    title={
                                      exercise?.is_completed
                                        ? "Completed"
                                        : "Not Completed"
                                    }
                                  >
                                    <Icon
                                      icon="fluent-mdl2:completed-solid"
                                      className="calendar-workout-card-complete-exercises-icon mb-1"
                                    />
                                  </Tooltip>
                                )}
                                Rounds For Time
                              </div>
                              {exercise?.emom_amrap?.map((interval) => {
                                return (
                                  <div className="my-1">
                                    <div style={{ fontSize: "8px" }}>
                                      Movements
                                    </div>
                                    {interval?.map((exerData) => {
                                      return (
                                        <div
                                          className="inner-div"
                                          style={{
                                            whiteSpace: "break-spaces",
                                            marginBottom: "3px",
                                          }}
                                        >
                                          {exerData?.exercise_info?.title}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        } else if (
                          exercise?.exercise_type == "reps_for_time" &&
                          exercise?.emom_amrap &&
                          exercise?.emom_amrap.length > 0
                        ) {
                          return (
                            <div className="workout-calendar-Superset-card inner-div my-1">
                              <div className="text-center">
                                {exercise?.is_completed && (
                                  <Tooltip
                                    title={
                                      exercise?.is_completed
                                        ? "Completed"
                                        : "Not Completed"
                                    }
                                  >
                                    <Icon
                                      icon="fluent-mdl2:completed-solid"
                                      className="calendar-workout-card-complete-exercises-icon mb-1"
                                    />
                                  </Tooltip>
                                )}
                                Reps For Time
                              </div>
                              {exercise?.emom_amrap?.map((interval) => {
                                return (
                                  <div className="my-1">
                                    <div style={{ fontSize: "8px" }}>
                                      Movements
                                    </div>
                                    {interval?.map((exerData) => {
                                      return (
                                        <div
                                          className="inner-div"
                                          style={{
                                            whiteSpace: "break-spaces",
                                            marginBottom: "3px",
                                          }}
                                        >
                                          {exerData?.exercise_info?.title}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        } else if (
                          exercise?.exercise_type == "amrap" &&
                          exercise?.emom_amrap &&
                          exercise?.emom_amrap.length > 0
                        ) {
                          return (
                            <div className="workout-calendar-Superset-card inner-div my-1">
                              <div className="text-center">AMRAP</div>
                              {exercise?.emom_amrap?.map((interval) => {
                                return (
                                  <div className="my-1">
                                    <div style={{ fontSize: "8px" }}>
                                      Movements
                                    </div>
                                    {interval?.map((exerData) => {
                                      return (
                                        <div
                                          className="inner-div"
                                          style={{
                                            whiteSpace: "break-spaces",
                                            marginBottom: "3px",
                                          }}
                                        >
                                          {exerData?.exercise_info?.title}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        } else if (
                          exercise?.exercise_type == "emom" &&
                          exercise?.emom_amrap &&
                          exercise?.emom_amrap.length > 0
                        ) {
                          return (
                            <div className="workout-calendar-Superset-card inner-div my-1">
                              <div className="text-center">EMOM</div>
                              {exercise?.emom_amrap?.map(
                                (interval, intervalIndex) => {
                                  return (
                                    <div className="my-1">
                                      <div style={{ fontSize: "8px" }}>
                                        Minute{" "}
                                        {getMinutesForInterval(
                                          index,
                                          intervalIndex,
                                        )}
                                      </div>
                                      {interval?.map((exerData) => {
                                        return (
                                          <div
                                            className="inner-div"
                                            style={{
                                              whiteSpace: "break-spaces",
                                              marginBottom: "3px",
                                            }}
                                          >
                                            {exerData?.exercise_info?.title}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                },
                              )}
                            </div>
                          );
                        } else if (
                          exercise?.sub_exercises &&
                          exercise?.sub_exercises.length > 0
                        ) {
                          return (
                            <div className="workout-calendar-Superset-card inner-div my-1">
                              {exercise?.sub_exercises?.map((exerData) => {
                                return (
                                  <div
                                    className="inner-div"
                                    style={{
                                      whiteSpace: "break-spaces",
                                      marginBottom: "3px",
                                    }}
                                  >
                                    {exerData?.exercise_info?.title}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        } else
                          return (
                            <div className="inner-div workout-calendar-exercise-card my-1">
                              <div
                                style={{
                                  whiteSpace: "break-spaces",
                                }}
                              >
                                {exercise?.exercise_info?.title}
                              </div>
                            </div>
                          );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        };

        const { isLoading: isWorkoutsLoading, refetch } = useQuery({
          queryKey: [
            "program_workouts",
            id,
            programmeData?.no_of_weeks,
            programmeData?.program_slug,
          ],
          enabled: !!programmeData?.program_slug,
          queryFn: async () => {
            let postData = {
              start_date: 0,
              end_date: parseInt(programmeData?.no_of_weeks) * 7,
              slug: programmeData?.program_slug,
              level_data: Levels,
            };
            const result = await programme_workout_list_api(postData);
            if (result.code === 200) {
              return result.program_workouts;
            } else {
              throw new Error(result.message);
            }
          },
          onSuccess: (data) => {
            const modifiedArray = data.map((item) => {
              return {
                ...item,
                open: globalOpenRef.current,
                editable: item?.workout_loggers?.length > 0 ? false : true,
              };
            });
            setworkouts(modifiedArray);

            if (initialEditWorkout && initialEditState) {
              const workout = modifiedArray.find(
                (w) => w._id === initialEditWorkout,
              );
              if (workout) {
                setEditWorkputData(workout);
                setSelectDate(workout?.workout_day);
                setEditItrationDrawerState(true);
              } else {
                // Workout not found (deleted) — clear modal params, keep date
                onModalProcessed({ type: "close" });
              }
            }
          },
          onError: (error) => {
            enqueueSnackbar(error.message, { variant: "error" });
          },
        });

        const getWorkoutListing = () => refetch();

        // Refetch silently when difficulty level filter changes (no full-screen loading)
        useEffect(() => {
          if (programmeData?.program_slug) {
            refetch();
          }
        }, [Levels]);

        const closeDragConfig = async (value) => {
          setOpenDragConfig(false);
          getWorkoutListing();
        };

        const closeCopyPasteConfig = async (value) => {
          setOpenCopyPasteConfig(false);
          setDataCopy(false);
          setPastedWorkouts([]);
          setSelectedWorkouts([]);
          getWorkoutListing();
        };

        const handleOpenMultiDelete = () => {
          setOpenMultiDelete(true);
        };

        const handleProgrammeMember = () => {
          setIsPastedWorkouts(true);
        };

        const handleCopyPaste = async (dayArg) => {
          setIsPasted(true);
          const workoutsIds = selectedWorkouts.map((workout) => workout?._id);

          const targetDay = dayArg !== undefined ? dayArg : pasteDate;

          const formDataObject = {
            copiedWorkoutIds: workoutsIds,
            workout_copy_type: "limited",
            targetStartDay: targetDay,
            target_program_id: programmeData._id,
          };

          try {
            const result = await paste_multiple_workouts(formDataObject);
            if (result.code === 200) {
              setPastedWorkouts(result?.copiedWorkouts);
              setSelectedWorkouts([]);
              enqueueSnackbar(result.message, { variant: "success" });
              setDataCopy(false);
              getWorkoutListing();
              if (SHOW_PASTE_CONFIRMATION) {
                setOpenCopyPasteConfig(false);
              }
              setIsPasted(false);
            } else {
              setSelectedWorkouts([]);
              enqueueSnackbar(result.message, { variant: "error" });
              setDataCopy(false);
              setIsPasted(false);
            }
          } catch (error) {
            setSelectedWorkouts([]);
            enqueueSnackbar("Failed to paste workouts", { variant: "error" });
            setDataCopy(false);
            setIsPasted(false);
          }
        };

        const handleDraged = async (value) => {
          setOpenDragConfig(false);
          const formDataObject = {
            title: DragConfigData?.title,
            description: DragConfigData?.description,
            exercises: DragConfigData?.exercises,
            library_type:
              DragConfigData?.practice_type === "workbook"
                ? DragConfigData?.library_type
                : "",
            questions: DragConfigData?.questions || [],
            workout_type: "",
            status: true,
            workout_day: DragConfigDate,
            video_url: DragConfigData?.video_url,
            video_thumbnail: DragConfigData?.video_thumbnail || {},
          };
          const formData = new FormData();
          formData.append("title", formDataObject.title);
          formData.append("description", formDataObject.description);
          formData.append(
            "exercises",
            JSON.stringify(formDataObject.exercises),
          );
          formData.append("workout_type", formDataObject.workout_type);
          formData.append("practice_type", DragConfigData?.practice_type);
          formData.append("comment", DragConfigData?.comment);
          formData.append(
            "target_day",
            DragConfigData?.target_day || "regular",
          );
          formData.append("meals", JSON.stringify(DragConfigData?.meals || []));
          formData.append("status", formDataObject.status);
          formData.append(
            "questions",
            JSON.stringify(formDataObject?.questions),
          );
          formData.append("library_type", formDataObject?.library_type);
          formData.append(
            "workout_days",
            JSON.stringify([formDataObject.workout_day]),
          );
          formData.append(
            "video_url",
            formDataObject.video_url ? formDataObject.video_url : "",
          );
          formData.append(
            "duplicate_image_path",
            JSON.stringify(formDataObject.video_thumbnail),
          );
          const result = await update_program_workout_api(
            formData,
            DragConfigData?.program_workout_slug,
          );

          if (result.code === 200) {
            setEditWorkputData(DragConfigData);
            getWorkoutListing();
            enqueueSnackbar(result.message, { variant: "success" });
            queryClient.invalidateQueries({ queryKey: ["program_workouts"] });
          } else {
            enqueueSnackbar(result.message, { variant: "error" });
            getWorkoutListing();
          }
        };
        const drag = (event, data) => {
          event.dataTransfer.setData("text/plain", JSON.stringify(data));
          event.target.style.opacity = "0.01";
        };
        const dragEnd = (event, data) => {
          event.dataTransfer.setData("text/plain", JSON.stringify(data));
          event.target.style.opacity = "";
        };
        const drop = (event, rowIndex, colIndex) => {
          event.preventDefault();
          const data = JSON.parse(event.dataTransfer.getData("text/plain"));
          const modifiedArray = workouts.map((item) => {
            if (item._id === data?._id) {
              return { ...item, workout_day: rowIndex * 7 + (colIndex + 1) };
            }
            return item;
          });
          setworkouts(modifiedArray);
          setOpenDragConfig(true);
          setDragConfigDate(rowIndex * 7 + (colIndex + 1));
          setDragConfigData(data);
        };

        function getNewCells(selectedCells, hoveredCell) {
          const cellDifferences = [];
          const maxCols = 7;

          for (let i = 1; i < selectedCells.length; i++) {
            const prevCell = selectedCells[i - 1];
            const currCell = selectedCells[i];

            const diffRow = currCell.rowIndex - prevCell.rowIndex;
            const diffCol = currCell.colIndex - prevCell.colIndex;

            cellDifferences.push({ rowDiff: diffRow, colDiff: diffCol });
          }

          const newCells = [
            { rowIndex: hoveredCell.rowIndex, colIndex: hoveredCell.colIndex },
          ];

          for (let diff of cellDifferences) {
            const lastCell = newCells[newCells.length - 1];

            let nextRowIndex = lastCell.rowIndex + diff.rowDiff;
            let nextColIndex = lastCell.colIndex + diff.colDiff;

            if (nextColIndex >= maxCols) {
              nextRowIndex += Math.floor(nextColIndex / maxCols);
              nextColIndex = nextColIndex % maxCols;
            } else if (nextColIndex < 0) {
              nextRowIndex -= Math.ceil(Math.abs(nextColIndex) / maxCols);
              nextColIndex = (maxCols + (nextColIndex % maxCols)) % maxCols;
            }

            if (
              nextColIndex === 0 &&
              newCells.some((cell) => cell.rowIndex === nextRowIndex)
            ) {
              nextColIndex = maxCols;
              nextRowIndex--;
            }

            newCells.push({ rowIndex: nextRowIndex, colIndex: nextColIndex });
          }

          return newCells;
        }

        useEffect(() => {
          if (selectedWorkouts.length > 0 && hoveredCell) {
            let selected_Cells = selectedWorkouts.map((workout) => {
              return {
                rowIndex: workout?.rowIndex,
                colIndex: workout?.colIndex,
              };
            });

            const newCells = getNewCells(selected_Cells, hoveredCell);
            document.querySelectorAll(".fixed-table-data").forEach((cell) => {
              cell.classList.remove("highlighted-hover");
            });
            if (newCells.length > 0) {
              document.querySelectorAll(".fixed-table-data").forEach((cell) => {
                const rowIndex = cell.getAttribute("data-row");
                const colIndex = cell.getAttribute("data-col");

                if (
                  newCells.some(
                    (cell) =>
                      cell.rowIndex == rowIndex && cell.colIndex == colIndex,
                  )
                ) {
                  cell.classList.add("highlighted-hover");
                }
              });
            }
          } else {
            document.querySelectorAll(".fixed-table-data").forEach((cell) => {
              cell.classList.remove("highlighted-hover");
            });
          }
        }, [selectedWorkouts, hoveredCell]);

        // Calculate width for fixed toolbar (subtract sidebar width)
        const screenWidth = window.innerWidth;
        const sidebarEl = document.querySelector(".simplebar-wrapper");
        const width_cal = sidebarEl
          ? screenWidth - sidebarEl.offsetWidth - 36
          : screenWidth;

        // Sync toolbar top with navbar on mount
        useEffect(() => {
          const getNavbarHeight = () => {
            const navbar =
              document.querySelector(".mini-drawer-menu-title") ||
              document.querySelector("header") ||
              document.querySelector("nav");
            return navbar ? navbar.getBoundingClientRect().height : 64;
          };
          const syncTop = () => {
            if (toolbarRowRef.current) {
              toolbarRowRef.current.style.top = `${getNavbarHeight()}px`;
            }
          };
          syncTop();
          const ro = new ResizeObserver(syncTop);
          if (toolbarRowRef.current) ro.observe(toolbarRowRef.current);
          return () => ro.disconnect();
        }, []);

        return (
          <div className="container-fluid" style={{ position: "relative" }}>
            {isPasted && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 9999,
                }}
              >
                <CircularProgress color="primary" />
              </div>
            )}
            <AddWorkoutPopup
              open={drawerState}
              setOpen={handleCloseAddModal}
              day={selectedDate}
              programme={programmeData}
              reloadList={getWorkoutListing}
              setEditWorkputData={setEditWorkputData}
              events={workouts}
              setEvents={setworkouts}
            />
            <EditWorkoutPopup
              open={editItrationDrawerState}
              setOpen={handleCloseEditModal}
              day={selectedDate}
              data={editWorkputData}
              programme={programmeData}
              reloadList={getWorkoutListing}
              events={workouts}
              setEvents={setworkouts}
              type="limited_program"
            />
            <ConfirmationWithDescription
              open={openDelete}
              setOpen={setOpenDelete}
              title={"Are you sure you want to Delete?"}
              descriotion={`If you take action it will delete from ${programmeData?.total_member_count} users.`}
              handleAgree={handleDelete}
            />
            <ConfirmationWithDescription
              open={openDragConfig}
              setOpen={closeDragConfig}
              title={"Do you want to take this action?"}
              descriotion={`It will impact ${programmeData?.total_member_count} users, who has access to this program.`}
              handleAgree={handleDraged}
            />
            <ConfirmationWithDescription
              open={openCopyPasteConfig}
              setOpen={closeCopyPasteConfig}
              title={"Do you want to paste it here?"}
              descriotion={`It will impact ${programmeData?.total_member_count} users, who has access to this program.`}
              handleAgree={handleCopyPaste}
            />

            <ConfirmationWithDescription
              open={openMultiDelete}
              setOpen={setOpenMultiDelete}
              title={"Are you sure you want to Delete?"}
              descriotion=""
              handleAgree={handleDeleteMultipul}
            />

            {/* ── Fixed toolbar — always visible, even during loading ── */}
            <div
              className="row fixed_top"
              ref={toolbarRowRef}
              style={{ width: `${width_cal}px` }}
            >
              <div className="col-12 text-end my-2">
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", sm: "center" }}
                  flexWrap="wrap"
                >
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {/* placeholder — selection buttons moved to floating */}
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                  >
                    {workouts.length > 0 && (
                      <>
                        <Button
                          size="small"
                          onClick={() => {
                            setGlobalOpen(true);
                            globalOpenRef.current = true;
                            setworkouts((prev) =>
                              prev.map((e) => ({ ...e, open: true })),
                            );
                          }}
                          startIcon={<KeyboardArrowDownIcon />}
                          sx={{
                            bgcolor: "grey.700",
                            color: "#fff",
                            borderRadius: "8px",
                            px: 1,
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                            py: 0.5,
                            "&:hover": { bgcolor: "grey.900" },
                          }}
                        >
                          Expand All
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setGlobalOpen(false);
                            globalOpenRef.current = false;
                            setworkouts((prev) =>
                              prev.map((e) => ({ ...e, open: false })),
                            );
                          }}
                          startIcon={<KeyboardArrowUpIcon />}
                          sx={{
                            bgcolor: "grey.700",
                            color: "#fff",
                            borderRadius: "8px",
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                            px: 1,
                            py: 0.5,
                            "&:hover": { bgcolor: "grey.900" },
                          }}
                        >
                          Collapse All
                        </Button>
                      </>
                    )}
                    <Tooltip title="Open Notepad" arrow>
                      <IconButton
                        size="small"
                        onClick={handleOpen}
                        sx={{
                          bgcolor: "primary.main",
                          color: "#fff",
                          borderRadius: "8px",
                          px: 1.2,
                          py: 0.6,
                          "&:hover": { bgcolor: "primary.dark" },
                        }}
                      >
                        <NoteAltOutlinedIcon fontSize="small" />
                        <span
                          style={{
                            fontSize: 12,
                            marginLeft: 4,
                            fontWeight: 500,
                          }}
                        >
                          Notepad
                        </span>
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Set Review Date" arrow>
                      <IconButton
                        size="small"
                        onClick={() => setIsReviewOpen(true)}
                        sx={{
                          bgcolor: "primary.main",
                          color: "#fff",
                          borderRadius: "8px",
                          px: 1.2,
                          py: 0.6,
                          "&:hover": { bgcolor: "primary.dark" },
                        }}
                      >
                        <EventAvailableOutlinedIcon fontSize="small" />
                        <span
                          style={{
                            fontSize: 12,
                            marginLeft: 4,
                            fontWeight: 500,
                          }}
                        >
                          Review Date
                        </span>
                      </IconButton>
                    </Tooltip>

                    {programmeData?.difficulty_level && (
                      <FormControl
                        sx={{
                          minWidth: 150,
                          "& .MuiOutlinedInput-root": {
                            height: "30px",
                            fontSize: 12,
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: 12,
                            top: "-3px",
                            "&.MuiInputLabel-shrink": { top: 0 },
                          },
                          flexShrink: 0,
                        }}
                        size="small"
                      >
                        <InputLabel id="fc-difficulty-label">
                          Difficulty Levels
                        </InputLabel>
                        <Select
                          labelId="fc-difficulty-label"
                          id="fc-difficulty-select"
                          multiple
                          name="difficulty_levels"
                          value={difficultyLevel}
                          label="Difficulty Levels"
                          onChange={(e) => setDifficultyLevel(e.target.value)}
                        >
                          <MenuItem value="novice">Novice</MenuItem>
                          <MenuItem value="intermediate">Intermediate</MenuItem>
                          <MenuItem value="advanced">Advanced</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Stack>
                </Stack>
              </div>
            </div>
            {/* Spacer to prevent content jump under fixed toolbar */}
            <div
              style={{
                height: toolbarRowRef.current
                  ? toolbarRowRef.current.offsetHeight
                  : 50,
              }}
            />

            {isWorkoutsLoading ? (
              <CircularProgress className={classes.loading} color="primary" />
            ) : (
              <div className="row">
                <table className="fixedTable" id="dragTable">
                  <thead>
                    <tr>
                      {[...Array(numCols)].map((_, index) => (
                        <th className="fixed-table-head" key={index}>
                          <div className="mt-2 mb-2" style={{ opacity: 0.7 }}>
                            Day {index + 1}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((row, rowIndex) => (
                      <tr key={rowIndex} className="">
                        {row.map((col, colIndex) => (
                          <td
                            className="fixed-table-data"
                            data-row={rowIndex}
                            data-col={colIndex}
                            key={colIndex}
                            onMouseEnter={() =>
                              handleCellHover(rowIndex, colIndex)
                            }
                            onMouseLeave={handleCellLeave}
                            onDrop={(event) => drop(event, rowIndex, colIndex)}
                            onDragOver={(event) => event.preventDefault()}
                          >
                            <div
                              style={{
                                position: "relative",
                                height: "100%",
                                width: "100%",
                                marginTop: 15,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: "600",
                                  ml: 0.5,
                                  mt: 2,
                                  opacity: 0.6,
                                }}
                                variant="body2"
                              >
                                Week {rowIndex + 1}
                              </Typography>
                              <div style={{ minHeight: "100px" }}>
                                {hoveredCell &&
                                  hoveredCell.rowIndex === rowIndex &&
                                  hoveredCell.colIndex === colIndex && (
                                    <div className="cell-icon-workout d-flex text-end w-100">
                                      {dataCopy && (
                                        <Tooltip
                                          touch="on"
                                          placement="bottom"
                                          title={"Paste"}
                                        >
                                          <div
                                            className="paste-icon-workout-general me-1"
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              minWidth: "19px",
                                              minHeight: "19px",
                                            }}
                                          >
                                            <AssignmentTurnedInIcon
                                              onClick={() =>
                                                handlePaste(
                                                  rowIndex + 1,
                                                  colIndex + 1,
                                                )
                                              }
                                              sx={{
                                                cursor: "pointer",
                                                height: 15,
                                                width: 15,
                                              }}
                                            />
                                          </div>
                                        </Tooltip>
                                      )}

                                      <AddIcon
                                        className="add-icon-workout-general"
                                        onClick={() =>
                                          handleOpenDrawer(
                                            rowIndex + 1,
                                            colIndex + 1,
                                          )
                                        }
                                        sx={{
                                          mr: 0.5,
                                          cursor: "pointer",
                                        }}
                                      />
                                    </div>
                                  )}
                                {workouts.map((data, i) => {
                                  if (
                                    data?.workout_day ==
                                    rowIndex * 7 + (colIndex + 1)
                                  )
                                    return (
                                      <div
                                        className="m-1"
                                        style={{ position: "relative" }}
                                        id={data.id + colIndex}
                                        class="draggable"
                                        draggable={data.editable}
                                        onDragStart={(event) =>
                                          data.editable && drag(event, data)
                                        }
                                        onDragEnd={(event) =>
                                          data.editable && dragEnd(event, data)
                                        }
                                      >
                                        <EventCard
                                          rowIndex={rowIndex}
                                          colIndex={colIndex}
                                          data={data}
                                          index={i}
                                          setData={setworkouts}
                                        />
                                      </div>
                                    );
                                })}
                              </div>
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <CustomPopoverNew
              isOpenPop={isPastedWorkouts}
              isClosePop={setIsPastedWorkouts}
              title="Add To Programme/Member"
              componentToPassDown={
                <div className="px-4 pb-3">
                  <PastedWorkouts
                    selectedWorkouts={selectedWorkouts}
                    setSelectedWorkouts={setSelectedWorkouts}
                    setDataCopy={setDataCopy}
                    setIsPastedWorkouts={setIsPastedWorkouts}
                  />
                </div>
              }
            />
            <CustomPopoverNew
              width="550px"
              isOpenPop={isReviewOpen}
              isClosePop={setIsReviewOpen}
              title="Review Date"
              componentToPassDown={
                <div className="px-4 pb-3">
                  <ReviewsDate
                    programme={programmeData}
                    isClosePop={setIsReviewOpen}
                    view="program"
                  />
                </div>
              }
            />

            {/* Floating action buttons — shown when workouts are selected */}
            {selectedWorkouts.length > 0 && (
              <div
                style={{
                  position: "fixed",
                  bottom: 24,
                  right: 24,
                  zIndex: 99999,
                  display: "flex",
                  flexDirection: "row",
                  gap: 10,
                  alignItems: "center",
                }}
              >
                <Tooltip title="Delete selected workouts" arrow placement="top">
                  <IconButton
                    size="small"
                    onClick={handleOpenMultiDelete}
                    sx={{
                      bgcolor: "error.main",
                      color: "#fff",
                      borderRadius: "8px",
                      px: 1.5,
                      py: 0.8,
                      boxShadow: 4,
                      "&:hover": { bgcolor: "error.dark" },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                    <span
                      style={{ fontSize: 12, marginLeft: 4, fontWeight: 500 }}
                    >
                      Delete ({selectedWorkouts.length})
                    </span>
                  </IconButton>
                </Tooltip>

                <Tooltip
                  title="Copy to Programme / Member"
                  arrow
                  placement="top"
                >
                  <IconButton
                    size="small"
                    onClick={handleProgrammeMember}
                    sx={{
                      bgcolor: "primary.main",
                      color: "#fff",
                      borderRadius: "8px",
                      px: 1.5,
                      py: 0.8,
                      boxShadow: 4,
                      "&:hover": { bgcolor: "primary.dark" },
                    }}
                  >
                    <ContentPasteIcon fontSize="small" />
                    <span
                      style={{ fontSize: 12, marginLeft: 4, fontWeight: 500 }}
                    >
                      Copy
                    </span>
                  </IconButton>
                </Tooltip>

                <Tooltip title="Discard selection" arrow placement="top">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedWorkouts([]);
                      setDataCopy(false);
                    }}
                    sx={{
                      bgcolor: "grey.600",
                      color: "#fff",
                      borderRadius: "8px",
                      px: 1.5,
                      py: 0.8,
                      boxShadow: 4,
                      "&:hover": { bgcolor: "grey.800" },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                    <span
                      style={{ fontSize: 12, marginLeft: 4, fontWeight: 500 }}
                    >
                      Discard
                    </span>
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </div>
        );
      }),
    [programmeData],
  );

  if (isLoading) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <div className="col-lg-12 col-md-12 full-calendar order-1 order-lg-1">
        <Table
          numRows={parseInt(programmeData?.no_of_weeks)}
          numCols={7}
          onModalProcessed={handleModalProcessed}
          initialDrawerState={initialDrawerState}
          initialDrawerDay={initialDrawerDay}
          initialEditState={initialEditState}
          initialEditWorkout={initialEditWorkout}
        />
      </div>
    </div>
  );
};

export default DynamicTable;
